import type { ExportIntegrationTickerIdentifiers, UserInstrumentDto } from "@eu.mdotm.api/sphere";
import type { Map as ImmutableMap } from "immutable";
import { groupBy } from "@mdotm/utils/collections";
import type {
	PortfolioLabComposition,
	PortfolioLabCompositionInstrument,
	PortfolioLabCompositionPortfolio,
} from "../../ctx";
import { typedObjectEntries } from "@mdotm/shadcn/legacy/utils";
import { labSub, labSum } from "../../utils/math";
import { classOf } from "./classify";

export type CompositionRowType = "group" | "portfolio" | "subgroup" | "single-instruments" | "instrument";

export type CompositionRow = {
	rowType: CompositionRowType;
	path: string[];
	label: string;
	weight: number;
	monetaryWeight: number;
	delta: number;
	allocationLabel?: string;
	portfolioUuid?: string;
	portfolioName?: string;
	tickerName?: string;
	identifiers?: ExportIntegrationTickerIdentifiers;
};

export const CompositionViewMode = {
	allocation: "ALLOCATION",
	holdings: "HOLDINGS",
	ungroup: "UNGROUP",
	holdingsUngroup: "HOLDINGS_UNGROUP",
} as const;

export type CompositionViewMode = (typeof CompositionViewMode)[keyof typeof CompositionViewMode];

export type BuildCompositionRowsParams = {
	composition: PortfolioLabComposition[];
	initialComposition: PortfolioLabComposition[];
	instrumentPool: ImmutableMap<string, UserInstrumentDto>;
	viewMode: CompositionViewMode;
	classificationUuid: string;
	investmentName: string;
};

type CompositionItem = {
	group: string;
	tickerName: string;
	effectiveWeight: number;
	effectiveMonetary: number;
	initialEffectiveWeight: number;
	portfolioUuid: string | null;
	portfolioName: string | null;
};

function createCompositionRowsBuilder() {
	// --- Row factories (private) ---

	function makeAllocationRow(
		label: string,
		weight: number,
		initialWeight: number,
		monetaryWeight: number,
	): CompositionRow {
		return {
			rowType: "group",
			path: [label],
			label,
			weight,
			monetaryWeight,
			delta: labSub(weight, initialWeight),
			allocationLabel: label,
		};
	}

	function makePortfolioSliceRow(
		path: string[],
		label: string,
		portfolioUuid: string | undefined,
		weight: number,
		initialWeight: number,
		monetaryWeight: number,
		allocationLabel?: string,
	): CompositionRow {
		return {
			rowType: "portfolio",
			path,
			label,
			weight,
			monetaryWeight,
			delta: labSub(weight, initialWeight),
			portfolioUuid,
			allocationLabel,
		};
	}

	function makeDirectBucketRow(weight: number, initialWeight: number, monetaryWeight: number): CompositionRow {
		return {
			rowType: "single-instruments",
			path: ["Single instrument"],
			label: "Single instrument",
			weight,
			monetaryWeight,
			delta: labSub(weight, initialWeight),
		};
	}

	function makeHoldingsGroupRow(
		parentLabel: string,
		groupLabel: string,
		portfolioUuid: string | undefined,
		weight: number,
		initialWeight: number,
		monetaryWeight: number,
	): CompositionRow {
		return {
			rowType: "subgroup",
			path: [parentLabel, groupLabel],
			label: groupLabel,
			weight,
			monetaryWeight,
			delta: labSub(weight, initialWeight),
			allocationLabel: groupLabel,
			portfolioUuid,
		};
	}

	function makeInstrumentRow(
		path: string[],
		tickerName: string,
		weight: number,
		initialWeight: number,
		monetaryWeight: number,
		opts?: {
			allocationLabel?: string;
			portfolioUuid?: string;
			portfolioName?: string;
			instrumentName?: string;
			identifiers?: ExportIntegrationTickerIdentifiers;
		},
	): CompositionRow {
		const { instrumentName, ...restOpts } = opts ?? {};
		return {
			rowType: "instrument",
			path,
			label: instrumentName ?? tickerName,
			weight,
			monetaryWeight,
			delta: labSub(weight, initialWeight),
			tickerName,
			...restOpts,
		};
	}

	// --- Builders ---

	function buildAllocationRows(
		investmentLabComposition: PortfolioLabComposition[],
		initialInvestmentLabComposition: PortfolioLabComposition[],
		instrumentPool: ImmutableMap<string, UserInstrumentDto>,
		classificationUuid: string,
	): CompositionRow[] {
		const composition: CompositionItem[] = investmentLabComposition.flatMap((instrument) => {
			if (instrument.type === "instrument") {
				const initialMatchingInstrument = initialInvestmentLabComposition.find(
					(c): c is PortfolioLabCompositionInstrument =>
						c.type === "instrument" && c.tickerName === instrument.tickerName,
				);
				return [
					{
						group: classOf(instrument.tickerName, instrumentPool, classificationUuid),
						tickerName: instrument.tickerName,
						effectiveWeight: instrument.weight,
						effectiveMonetary: instrument.monetaryWeight,
						initialEffectiveWeight: initialMatchingInstrument?.weight ?? 0,
						portfolioUuid: null,
						portfolioName: null,
					},
				];
			}

			if (instrument.type === "portfolio") {
				const initialMatchingInvestment = initialInvestmentLabComposition.find(
					(c): c is PortfolioLabCompositionPortfolio => c.type === "portfolio" && c.uuid === instrument.uuid,
				);
				return instrument.instruments.map(
					(investmentInstrument): CompositionItem => ({
						group: classOf(investmentInstrument.tickerName, instrumentPool, classificationUuid),
						tickerName: investmentInstrument.tickerName,
						effectiveWeight: investmentInstrument.weight,
						effectiveMonetary: investmentInstrument.monetaryWeight,
						initialEffectiveWeight:
							initialMatchingInvestment?.instruments.find((i) => i.tickerName === investmentInstrument.tickerName)
								?.weight ?? 0,
						portfolioUuid: instrument.uuid,
						portfolioName: instrument.name,
					}),
				);
			}

			return [];
		});

		const compositionsMapByAllocationLabel = groupBy(composition, (item) => item.group);
		return typedObjectEntries(compositionsMapByAllocationLabel).flatMap(([allocationLabel, instrumentsSlice = []]) => {
			const classificationWeight = labSum(instrumentsSlice, (i) => i.effectiveWeight);
			const classificationMonetaryWeight = labSum(instrumentsSlice, (i) => i.effectiveMonetary);
			const classificationInitialWeight = labSum(instrumentsSlice, (i) => i.initialEffectiveWeight);

			const { investments = [], instruments = [] } = groupBy(instrumentsSlice, (item): "investments" | "instruments" =>
				item.portfolioUuid != null ? "investments" : "instruments",
			);

			const investmentsMapByUuid = groupBy(investments, (item) => item.portfolioUuid!);

			const investmentRows = typedObjectEntries(investmentsMapByUuid).flatMap(
				([portfolioUuid, instrumentsByUuid = []]) => {
					const portfolioName = instrumentsByUuid[0].portfolioName!;
					const sliceWeight = labSum(instrumentsByUuid, (i) => i.effectiveWeight);
					const sliceMonetary = labSum(instrumentsByUuid, (i) => i.effectiveMonetary);
					const sliceInitialWeight = labSum(instrumentsByUuid, (i) => i.initialEffectiveWeight);
					return [
						makePortfolioSliceRow(
							[allocationLabel, portfolioName],
							portfolioName,
							portfolioUuid,
							sliceWeight,
							sliceInitialWeight,
							sliceMonetary,
							allocationLabel,
						),
						...instrumentsByUuid.map((item) =>
							makeInstrumentRow(
								[allocationLabel, portfolioName, item.tickerName],
								item.tickerName,
								item.effectiveWeight,
								item.initialEffectiveWeight,
								item.effectiveMonetary,
								{
									allocationLabel: allocationLabel,
									portfolioUuid,
									instrumentName: instrumentPool.get(item.tickerName)?.name,
									identifiers: instrumentPool.get(item.tickerName)?.identifiers,
								},
							),
						),
					];
				},
			);

			const instrumentsRows = instruments.map((item) =>
				makeInstrumentRow(
					[allocationLabel, item.tickerName],
					item.tickerName,
					item.effectiveWeight,
					item.initialEffectiveWeight,
					item.effectiveMonetary,
					{
						allocationLabel: allocationLabel,
						instrumentName: instrumentPool.get(item.tickerName)?.name,
						identifiers: instrumentPool.get(item.tickerName)?.identifiers,
					},
				),
			);

			return [
				makeAllocationRow(
					allocationLabel,
					classificationWeight,
					classificationInitialWeight,
					classificationMonetaryWeight,
				),
				...investmentRows,
				...instrumentsRows,
			];
		});
	}

	function buildHoldingsRows(
		investmentLabComposition: PortfolioLabComposition[],
		initialComposition: PortfolioLabComposition[],
		instrumentPool: ImmutableMap<string, UserInstrumentDto>,
		classificationUuid?: string,
	): CompositionRow[] {
		const instruments = investmentLabComposition.filter(
			(c): c is PortfolioLabCompositionInstrument => c.type === "instrument",
		);
		const portfolios = investmentLabComposition.filter(
			(c): c is PortfolioLabCompositionPortfolio => c.type === "portfolio",
		);

		const portfolioRows = portfolios.flatMap((portfolio) => {
			const initialInvestmentComposition = initialComposition.find(
				(c): c is PortfolioLabCompositionPortfolio => c.type === "portfolio" && c.uuid === portfolio.uuid,
			);

			const portfolioSliceRow = makePortfolioSliceRow(
				[portfolio.name],
				portfolio.name,
				portfolio.uuid,
				portfolio.weight,
				initialInvestmentComposition?.weight ?? 0,
				portfolio.monetaryWeight,
			);

			if (classificationUuid == null) {
				return [
					portfolioSliceRow,
					...portfolio.instruments.map((instrument) =>
						makeInstrumentRow(
							[portfolio.name, instrument.tickerName],
							instrument.tickerName,
							instrument.weight,
							initialInvestmentComposition?.instruments.find((i) => i.tickerName === instrument.tickerName)?.weight ??
								0,
							instrument.monetaryWeight,
							{
								portfolioUuid: portfolio.uuid,
								instrumentName: instrumentPool.get(instrument.tickerName)?.name,
								identifiers: instrumentPool.get(instrument.tickerName)?.identifiers,
							},
						),
					),
				];
			}

			const instrumentsGroupedByClassification = groupBy(portfolio.instruments, (inner) =>
				classOf(inner.tickerName, instrumentPool, classificationUuid),
			);

			const groupRows = typedObjectEntries(instrumentsGroupedByClassification).flatMap(
				([classificationLabel, instrumentsInGroup = []]) => {
					const groupWeight = labSum(instrumentsInGroup, (i) => i.weight);
					const groupMonetary = labSum(instrumentsInGroup, (i) => i.monetaryWeight);
					const groupInitialWeight = labSum(
						instrumentsInGroup,
						(i) =>
							initialInvestmentComposition?.instruments.find((init) => init.tickerName === i.tickerName)?.weight ?? 0,
					);

					return [
						makeHoldingsGroupRow(
							portfolio.name,
							classificationLabel,
							portfolio.uuid,
							groupWeight,
							groupInitialWeight,
							groupMonetary,
						),
						...instrumentsInGroup.map((instrument) => {
							const instrumentWeightInGroup = instrument.weight;
							const initialInstrumentWeightInGroup =
								initialInvestmentComposition?.instruments.find((init) => init.tickerName === instrument.tickerName)
									?.weight ?? 0;
							return makeInstrumentRow(
								[portfolio.name, classificationLabel, instrument.tickerName],
								instrument.tickerName,
								instrumentWeightInGroup,
								initialInstrumentWeightInGroup,
								instrument.monetaryWeight,
								{
									allocationLabel: classificationLabel,
									portfolioUuid: portfolio.uuid,
									instrumentName: instrumentPool.get(instrument.tickerName)?.name,
									identifiers: instrumentPool.get(instrument.tickerName)?.identifiers,
								},
							);
						}),
					];
				},
			);

			return [portfolioSliceRow, ...groupRows];
		});

		if (instruments.length === 0) {
			return portfolioRows;
		}

		const singleWeight = labSum(instruments, (i) => i.weight);
		const singleMonetary = labSum(instruments, (i) => i.monetaryWeight);
		const singleInitialWeight = labSum(instruments, (i) => {
			const matchedInitialInvestmentComposition = initialComposition.find(
				(c): c is PortfolioLabCompositionInstrument => c.type === "instrument" && c.tickerName === i.tickerName,
			);
			return matchedInitialInvestmentComposition?.weight ?? 0;
		});

		if (classificationUuid == null) {
			const directInstrumentRows = instruments.map((direct) => {
				const init = initialComposition.find(
					(c): c is PortfolioLabCompositionInstrument => c.type === "instrument" && c.tickerName === direct.tickerName,
				);
				return makeInstrumentRow(
					["Single instrument", direct.tickerName],
					direct.tickerName,
					direct.weight,
					init?.weight ?? 0,
					direct.monetaryWeight,
					{
						instrumentName: instrumentPool.get(direct.tickerName)?.name,
						identifiers: instrumentPool.get(direct.tickerName)?.identifiers,
					},
				);
			});

			return [
				...portfolioRows,
				makeDirectBucketRow(singleWeight, singleInitialWeight, singleMonetary),
				...directInstrumentRows,
			];
		}

		const instrumentsByClassification = groupBy(instruments, (i) =>
			classOf(i.tickerName, instrumentPool, classificationUuid),
		);

		const singleInstrumentGroupRows = typedObjectEntries(instrumentsByClassification).flatMap(
			([classificationLabel, instrumentsInGroup = []]) => {
				const classificationWeight = labSum(instrumentsInGroup, (i) => i.weight);
				const classificationMonetaryWeight = labSum(instrumentsInGroup, (i) => i.monetaryWeight);
				const classificationInitialWeight = labSum(instrumentsInGroup, (i) => {
					const init = initialComposition.find(
						(c): c is PortfolioLabCompositionInstrument => c.type === "instrument" && c.tickerName === i.tickerName,
					);
					return init?.weight ?? 0;
				});

				return [
					makeHoldingsGroupRow(
						"Single instrument",
						classificationLabel,
						undefined,
						classificationWeight,
						classificationInitialWeight,
						classificationMonetaryWeight,
					),
					...instrumentsInGroup.map((direct) => {
						const init = initialComposition.find(
							(c): c is PortfolioLabCompositionInstrument =>
								c.type === "instrument" && c.tickerName === direct.tickerName,
						);
						return makeInstrumentRow(
							["Single instrument", classificationLabel, direct.tickerName],
							direct.tickerName,
							direct.weight,
							init?.weight ?? 0,
							direct.monetaryWeight,
							{
								allocationLabel: classificationLabel,
								instrumentName: instrumentPool.get(direct.tickerName)?.name,
								identifiers: instrumentPool.get(direct.tickerName)?.identifiers,
							},
						);
					}),
				];
			},
		);

		return [
			...portfolioRows,
			makeDirectBucketRow(singleWeight, singleInitialWeight, singleMonetary),
			...singleInstrumentGroupRows,
		];
	}

	function buildUngroupRows(
		investmentsLab: PortfolioLabComposition[],
		initialComposition: PortfolioLabComposition[],
		instrumentPool: ImmutableMap<string, UserInstrumentDto>,
	): CompositionRow[] {
		return investmentsLab.flatMap((instrument) => {
			if (instrument.type === "instrument") {
				const initialMatchingInstrument = initialComposition.find(
					(c): c is PortfolioLabCompositionInstrument =>
						c.type === "instrument" && c.tickerName === instrument.tickerName,
				);
				return [
					makeInstrumentRow(
						[instrument.tickerName],
						instrument.tickerName,
						instrument.weight,
						initialMatchingInstrument?.weight ?? 0,
						instrument.monetaryWeight,
						{
							instrumentName: instrumentPool.get(instrument.tickerName)?.name,
							identifiers: instrumentPool.get(instrument.tickerName)?.identifiers,
						},
					),
				];
			}

			const initialInvestmentComposition = initialComposition.find(
				(c): c is PortfolioLabCompositionPortfolio => c.type === "portfolio" && c.uuid === instrument.uuid,
			);
			return instrument.instruments.map((investmentInstrument) => {
				const initialInstrumentWeight =
					initialInvestmentComposition?.instruments.find((i) => i.tickerName === investmentInstrument.tickerName)
						?.weight ?? 0;
				return makeInstrumentRow(
					[`${instrument.uuid}/${investmentInstrument.tickerName}`],
					investmentInstrument.tickerName,
					investmentInstrument.weight,
					initialInstrumentWeight,
					investmentInstrument.monetaryWeight,
					{
						portfolioUuid: instrument.uuid,
						portfolioName: instrument.name,
						instrumentName: instrumentPool.get(investmentInstrument.tickerName)?.name,
						identifiers: instrumentPool.get(investmentInstrument.tickerName)?.identifiers,
					},
				);
			});
		});
	}

	return { buildAllocationRows, buildHoldingsRows, buildUngroupRows };
}

export type CompositionRowsBuilder = ReturnType<typeof createCompositionRowsBuilder>;

const compositionRowsBuilder = createCompositionRowsBuilder();

export type VirtualUuidData = {
	weight: number | null;
	monetaryWeight: number | null;
	delta: number | null;
	rowType: CompositionRowType | undefined;
	allocationLabel: string | undefined;
	portfolioUuid: string | undefined;
	portfolioName: string | undefined;
	tickerName: string | undefined;
	portfolioIdentifier: ExportIntegrationTickerIdentifiers | undefined;
};

export type MergedCompositionRow = {
	path: string[];
	label: string;
	data: Record<string, VirtualUuidData>;
};

export function buildMergedCompositionRows(
	inputs: Array<{ virtualUuid: string; rows: CompositionRow[] }>,
): MergedCompositionRow[] {
	const pathMap = new Map<string, MergedCompositionRow>();

	for (const { virtualUuid, rows } of inputs) {
		for (const row of rows) {
			const pathKey = row.path.join("/");
			let merged = pathMap.get(pathKey);
			if (!merged) {
				merged = {
					path: row.path,
					label: row.label,
					data: {},
				};
				pathMap.set(pathKey, merged);
			}
			merged.data[virtualUuid] = {
				weight: row.weight,
				monetaryWeight: row.monetaryWeight,
				delta: row.delta,
				rowType: row.rowType,
				allocationLabel: row.allocationLabel,
				portfolioUuid: row.portfolioUuid,
				portfolioName: row.portfolioName,
				tickerName: row.tickerName,
				portfolioIdentifier: row.identifiers,
			};
		}
	}

	return Array.from(pathMap.values());
}

export function buildCompositionRows(params: BuildCompositionRowsParams): CompositionRow[] {
	const { composition, initialComposition, instrumentPool, viewMode, classificationUuid } = params;

	if (viewMode === CompositionViewMode.holdingsUngroup) {
		return compositionRowsBuilder.buildHoldingsRows(composition, initialComposition, instrumentPool);
	}

	if (viewMode === CompositionViewMode.ungroup) {
		return compositionRowsBuilder.buildUngroupRows(composition, initialComposition, instrumentPool);
	}

	if (viewMode === CompositionViewMode.holdings) {
		return compositionRowsBuilder.buildHoldingsRows(
			composition,
			initialComposition,
			instrumentPool,
			classificationUuid,
		);
	}

	return compositionRowsBuilder.buildAllocationRows(
		composition,
		initialComposition,
		instrumentPool,
		classificationUuid,
	);
}
